import { useShoppingCart } from "../context/ShoppingCartContext";
import { pf } from "../utilities/helpers";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import usePageLanguage from "../hooks/usePageLanguage";
import React from "react";

type CartItemProps = {
  id: number;
  quantity: number;
  deleteHidden?: boolean;
  start?: string;
  eventId?: number;
};

export function CartItemRow({
  id,
  quantity,
  deleteHidden,
  eventId,
  start,
}: CartItemProps) {
  const {
    locationProducts,
    decreaseCartQuantity,
    setCartQuantity,
    increaseCartQuantity,
    removeFromCart,
    getMaxAvailable,
  } = useShoppingCart();
  const locationProduct = locationProducts.find((i) => i.id === id);
  const lang = usePageLanguage();

  // Get maxAvailable for event products
  const maxAvailable = eventId ? getMaxAvailable(id, eventId) : null;

  // Check if we can increase quantity (respecting maxAvailable for event products)
  const canIncreaseQuantity = maxAvailable === null || quantity < maxAvailable;

  if (locationProduct == null) return null;

  return (
    <div className="cartOrderProductRow">
      <div
        className="cartOrderProductRowImage "
        style={{
          paddingTop: deleteHidden ? "5px" : "0px",
          paddingRight: deleteHidden ? "5px" : "0px",
        }}
      >
        <img
          style={{
            width: deleteHidden ? "70px" : "80px",
            height: deleteHidden ? "70px" : "80px",
          }}
          src={locationProduct.firstFileUrl}
          className="cartSidepanelProductImage"
        />
      </div>
      <div
        className="cartOrderProductRowName"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <span>{locationProduct?.translations?.[lang].name}</span>
        {/* The format needs to be like this: N, 12.05.2025, 14:00 */}
        {/* Also we should add +3 hours to start date */}
        {start && (
          <span style={{ fontSize: "12px" }}>
            {new Date(start).toLocaleDateString("et-EE", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
            ,{" "}
            {new Date(
              new Date(start).getTime() + 0 * 60 * 60 * 1000
            ).toLocaleTimeString("et-EE", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
        {/* Show maxAvailable for event products */}
        {eventId && maxAvailable !== null && (
          <span style={{ fontSize: "12px", color: "#000" }}>
            Saadaval: {maxAvailable}
          </span>
        )}
      </div>
      <div
        className="cartOrderProductQuantity"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          justifyContent: "start",
          gap: "0px",
        }}
      >
        <div style={{ display: "flex", gap: "4px" }}>
          <RemoveIcon
            className="icon clickable padding-5 fs-14"
            style={{ marginTop: "4px" }}
            onClick={() => {
              if (quantity === 1) {
                removeFromCart(id, eventId ? eventId : undefined);
              } else {
                if (maxAvailable !== null && quantity > maxAvailable) {
                  setCartQuantity(
                    id,
                    maxAvailable,
                    eventId ? eventId : undefined,
                    start
                  );
                } else {
                  setCartQuantity(
                    id,
                    quantity - 1,
                    eventId ? eventId : undefined,
                    start
                  );
                }
              }
            }}
          />

          <div style={{ display: "flex", alignItems: "center" }}>
            {/* <span>{quantity}</span> */}
            <input
              type="text"
              name="quantity"
              disabled={start ? true : false}
              value={quantity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = e.target.value;

                // Allow empty input for typing purposes
                if (inputValue === "") {
                  return;
                }

                // Only allow numeric input
                const numericValue = parseInt(inputValue, 10);
                if (isNaN(numericValue)) {
                  return;
                }

                // Ensure value is at least 1
                let newQuantity = Math.max(1, numericValue);

                // Ensure value doesn't exceed maxAvailable for event products
                if (maxAvailable !== null && newQuantity > maxAvailable) {
                  newQuantity = maxAvailable;
                }

                // Update the cart quantity
                setCartQuantity(
                  id,
                  newQuantity,
                  eventId ? eventId : undefined,
                  start
                );
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                // On blur, ensure we have a valid value (at least 1)
                const inputValue = e.target.value;
                const numericValue = parseInt(inputValue, 10);

                if (isNaN(numericValue) || numericValue < 1) {
                  setCartQuantity(id, 1, eventId ? eventId : undefined, start);
                }
              }}
              size={Math.max(quantity.toString().length, 1)}
              style={{
                display: "inline-block",
                width: "auto",
                backgroundColor: "transparent",
                border: "none",
                textAlign: "center",
                color: "black",
              }}
            />{" "}
            <span>{locationProduct.unit_shortname} </span>
          </div>

          <AddIcon
            style={{
              marginTop: "4px",
              opacity: canIncreaseQuantity ? 1 : 0.4,
              cursor: canIncreaseQuantity ? "pointer" : "not-allowed",
            }}
            className="icon clickable padding-5 fs-14"
            onClick={() => {
              // Check if we can increase quantity (respect maxAvailable for event products)
              if (!canIncreaseQuantity) {
                return;
              }

              if (start) {
                console.log(locationProduct, "locationProduct");

                setCartQuantity(
                  id,
                  quantity + 1,
                  eventId ? eventId : undefined,
                  start
                );
              } else {
                increaseCartQuantity(
                  id,
                  null,
                  eventId ? eventId : undefined,
                  start
                );
              }
            }}
          />
        </div>

        {/* If product quantity is greater than maxAvailable, show a warning */}
        {start && maxAvailable !== null && quantity > maxAvailable && (
          <div
            style={{
              fontSize: "12px",
              color: "white",
              fontWeight: "600",
              backgroundColor: "#AE0000",
              padding: "3px 15px",
              borderRadius: "var(--shop-border-radius)",
            }}
          >
            Saadaval {maxAvailable}
          </div>
        )}
      </div>
      <div className="cartOrderProductRowPrice">
        <span style={{ width: "100%" }}>
          {pf(locationProduct.price_regular * quantity)} &euro;
        </span>
      </div>
      <div
        className="cartOrderProductRowDelete"
        style={{
          visibility: deleteHidden ? "hidden" : "visible",
        }}
      >
        <FontAwesomeIcon
          icon={faTrashAlt}
          onClick={() => removeFromCart(id, eventId ? eventId : undefined)}
        />
      </div>

      <div className="cartOrderProductRowDiscount">
        {locationProduct.adjustment_amount &&
        locationProduct.adjustments?.length > 0 ? (
          <span className="discount-price-color">
            {locationProduct.adjustments?.map((adjustment, i) => {
              return `${i > 0 ? ", " : ""}${adjustment.display_name} ${
                adjustment.value.toString().startsWith("-") ? "" : "+"
              }${adjustment.value}%`;
            })}
          </span>
        ) : (
          ""
        )}
      </div>

      <div className="cartOrderProductRowPrice">
        {locationProduct.adjustment_amount &&
        locationProduct.adjustments?.length > 0 ? (
          <span
            className="discount-price-color"
            style={{ width: "100%", fontWeight: "500" }}
          >
            {pf(Number(-locationProduct.adjustment_amount) * quantity)} &euro;
          </span>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}
